/**
 * Model Loader Module
 * Loads ONNX models and initializes inference sessions
 */

import * as ort from 'onnxruntime-web';
import { configureOnnxWasmRuntime, getExecutionProvider } from '../aiRuntime';

let sessionCache = null;
let currentModelPath = null;
let currentModelBuffer = null;

/**
 * Load model from File object
 * @param {File} file - ONNX model file
 * @returns {Promise<ArrayBuffer>}
 */
async function loadModelFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(new Error('Failed to read model file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Load ONNX model and create inference session
 * @param {string|File|ArrayBuffer} modelSource - Path to .onnx model file, File object, or ArrayBuffer
 * @param {string} device - 'webgpu' or 'wasm'
 * @returns {Promise<ort.InferenceSession>}
 */
export async function loadModel(modelSource, device = 'wasm') {
  let modelPath = null;
  let modelBuffer = null;
  let cacheKey = null;

  // Handle different input types
  if (modelSource instanceof File) {
    // File object - convert to ArrayBuffer
    console.log(`Loading model from file: ${modelSource.name}`);
    modelBuffer = await loadModelFile(modelSource);
    cacheKey = `file:${modelSource.name}:${modelSource.size}`;
  } else if (modelSource instanceof ArrayBuffer) {
    // ArrayBuffer
    console.log('Loading model from ArrayBuffer');
    modelBuffer = modelSource;
    cacheKey = `buffer:${modelSource.byteLength}`;
  } else if (typeof modelSource === 'string') {
    // String path (URL or local path)
    modelPath = modelSource;
    // Check if it's a local path (starts with /models/ or ./models/)
    if (modelSource.startsWith('/models/') || modelSource.startsWith('./models/')) {
      // Local model path - will be loaded by ONNX Runtime
      cacheKey = modelSource;
    } else if (modelSource.startsWith('http://') || modelSource.startsWith('https://')) {
      // URL - will be loaded by ONNX Runtime
      cacheKey = modelSource;
    } else {
      // Assume local path
      cacheKey = modelSource;
    }
  } else {
    throw new Error('Invalid model source. Expected string (path/URL), File, or ArrayBuffer');
  }

  // Debug cache state
  console.log('🔍 Cache check:');
  console.log('   - Has cached session:', !!sessionCache);
  console.log('   - Current cache key:', currentModelPath);
  console.log('   - Requested cache key:', cacheKey);
  console.log('   - Keys match:', currentModelPath === cacheKey);

  // Return cached session if same model
  if (sessionCache && currentModelPath === cacheKey) {
    console.log('✅ Using cached model session (instant load)');
    return sessionCache;
  }

  // Log if this is a new model load
  if (sessionCache) {
    console.log('⚠️  Model changed, loading new model...');
    console.log(`   - Previous: ${currentModelPath}`);
    console.log(`   - New: ${cacheKey}`);
  } else {
    console.log('📦 Loading model for the first time (this may take a few seconds)...');
  }

  try {
    configureOnnxWasmRuntime();
    const executionProviders = getExecutionProvider(device);

    console.log(`Loading model: ${cacheKey}`);
    console.log(`Using execution providers: ${executionProviders.join(', ')}`);

    const sessionOptions = {
      executionProviders: executionProviders,
      graphOptimizationLevel: 'all',
      enableCpuMemArena: true,
      enableMemPattern: true,
      // 0=Verbose, 1=Info, 2=Warning, 3=Error, 4=Fatal — use 2 so only warnings/errors show
      logSeverityLevel: 2,
      logVerbosityLevel: 2
    };

    // Load model - ONNX Runtime can handle URL, path, or ArrayBuffer
    let session;
    if (modelBuffer) {
      // Load from ArrayBuffer
      session = await ort.InferenceSession.create(modelBuffer, sessionOptions);
    } else {
      // Load from path/URL
      session = await ort.InferenceSession.create(modelPath, sessionOptions);
    }

    // Cache session
    sessionCache = session;
    currentModelPath = cacheKey;
    currentModelBuffer = modelBuffer;

    console.log('✅ Model loaded successfully');
    console.log('📋 Model Info:');
    console.log('   - Input names:', session.inputNames);
    console.log('   - Output names:', session.outputNames);
    console.log('   - Input metadata:', session.inputMetadata);
    console.log('   - Output metadata:', session.outputMetadata);

    // Verify which execution providers are actually being used
    console.log('🔧 Session created with execution providers:', executionProviders);

    if (device === 'webgpu') {
      console.log('✅ Session configured for WebGPU execution provider');
      console.log('📊 Inference will run on GPU (WebGPU)');
      console.log('💡 Tip: Model is now cached - subsequent runs will be instant!');

      // Check if model name suggests f16 quantization
      if (cacheKey && (cacheKey.includes('f16') || cacheKey.includes('q4f16'))) {
        console.warn('⚠️  WARNING: Model appears to use f16 (half-precision) quantization');
        console.warn('⚠️  WebGPU validation errors may occur if f16 extension is not supported');
        console.warn('💡 Suggestion: Try a non-f16 model (e.g., model_int8.onnx or model_q4.onnx)');
        console.warn('💡 Or: Inference may still work despite validation errors (check results)');
      }

      // Note: WASM warnings in console are normal - ONNX Runtime Web may load
      // WASM modules as fallback, but actual inference runs on WebGPU
      console.log('ℹ️  Note: WASM-related warnings are initialization noise, not actual execution');
    } else {
      console.log('📊 Inference will run on CPU (WASM)');
      console.log('💡 Tip: Model is now cached - subsequent runs will be instant!');
    }

    return session;
  } catch (error) {
    console.error('Error loading model:', error);
    throw new Error(`Failed to load model: ${error.message}`);
  }
}

/**
 * Clear cached model session
 */
export function clearModelCache() {
  sessionCache = null;
  currentModelPath = null;
  currentModelBuffer = null;
}

/**
 * Get model input shape from metadata
 * @param {ort.InferenceSession} session
 * @returns {number[]}
 */
export function getInputShape(session) {
  if (!session || !session.inputNames || session.inputNames.length === 0) {
    throw new Error('Session has no input names');
  }

  const inputName = session.inputNames[0];
  const inputMetadata = session.inputMetadata[inputName];

  if (!inputMetadata || !inputMetadata.dims) {
    console.warn('Input metadata not found, using default shape [1, 3, 384, 384]');
    return [1, 3, 384, 384];
  }

  return inputMetadata.dims;
}
